<script>
import { mapGetters } from 'vuex'

import Layout from '@layouts/main.vue'
import ListTick from '@assets/icons/list-tick.svg'
import StarsRating from '@components/stars-rating.vue'
import ReviewsSlider from '@components/common/sliders/reviews-slider.vue'
import ExampleSection from '@components/pass-and-card/example-section.vue'
import PassGrid from '@components/pass-and-card/pass-grid.vue'

import Translatable from '@src/mixins/translatable.js'
import SanitizerMixin from '@src/mixins/sanitizer-mixin.js'
import ImageMixin from '@src/mixins/imageMixin.js'
import Resizable from '@src/mixins/resizable.js'
import AuthorLabel from '@src/mixins/author.js'

export default {
  page: {
    title: "What's included?",
    meta: [
      {
        name: 'description',
        content:
          'Free entry to 80+ attractions, Free bus tour and river cruises, Special Offers, Free mobile app, Optional transport tickets',
      },
    ],
  },
  components: {
    Layout,
    StarsRating,
    ReviewsSlider,
    ExampleSection,
    PassGrid,
  },
  mixins: [Translatable, SanitizerMixin, ImageMixin, Resizable, AuthorLabel],
  computed: {
    ...mapGetters('passAndCard', ['pageData']),
    ...mapGetters('reviews', ['averageRating', 'fifteenRandomReviews']),
    getListStyleTick() {
      return {
        listStyleImage: `url(${ListTick})`,
      }
    },
    pageImg() {
      if (this.pageData.mainImage) {
        return this.desktopScreen
          ? this.pageData.mainImage.web_image
          : this.pageData.mainImage.app_image
      }
      return ''
    },
  },
  methods: {
    pageContent(prop) {
      if (this.pageData.content) {
        if (Array.isArray(this.pageData.content[this.currentLanguage][prop])) {
          return this.pageData.content[this.currentLanguage][prop].length
            ? this.pageData.content[this.currentLanguage][prop]
            : this.pageData.content.en[prop]
        }
        return this.pageData.content[this.currentLanguage][prop]
          ? this.pageData.content[this.currentLanguage][prop]
          : this.pageData.content.en[prop]
      }
      return []
    },
  },
}
</script>

<template>
  <Layout>
    <section class="header justify-center" :style="getBackground(pageImg)">
      <div class="header-block">
        <h1
          class="header-title"
          v-html="getTranslatedContent('title', pageData.content)"
        ></h1>
        <h3
          class="header-subtitle"
          v-html="getTranslatedContent('subtitle', pageData.content)"
        ></h3>
      </div>
    </section>
    <section id="what-you-get" class="description-banner justify-center">
      <p class="author container" v-html="authorLabel(pageData)"></p>
      <div class="description">
        <div class="description-card">
          <div class="banner-content">
            <h3
              class="banner-title"
              v-html="getTranslatedContent('benefits_title', pageData.content)"
            ></h3>
            <p
              class="banner-text"
              v-html="
                getTranslatedContent('benefits_description', pageData.content)
              "
            ></p>
          </div>
          <div class="banner-images">
            <div class="banner-card"></div>
            <div class="banner-phone"></div>
          </div>
        </div>
        <div class="description-card-list">
          <div class="tips-details">
            <ul :style="getListStyleTick">
              <template v-for="(benefit, index) in pageContent('benefits')">
                <li
                  :key="benefit.title + benefit.text + index"
                  v-html="benefit.title"
                ></li>
                <p
                  v-if="benefit.text"
                  :key="benefit.text + index"
                  class="subtext"
                  v-html="benefit.text"
                ></p>
              </template>
            </ul>
          </div>
        </div>
      </div>
    </section>
    <section class="grid-section justify-center">
      <PassGrid />
    </section>
    <section id="how-you-save" class="steps-section justify-center">
      <div class="container">
        <h3
          class="section-title"
          v-html="getTranslatedContent('PASS_AND_CARD_SAVINGS_SECTION_TITLE')"
        >
        </h3>
        <p class="saving-text" v-html="pageContent('example_days_text')"></p>
        <h4
          class="using-title"
          v-html="getTranslatedContent('PASS_AND_CARD_example_interapy')"
        >
        </h4>
        <ExampleSection />
      </div>
    </section>
    <section class="reviews-section justify-center">
      <div class="container">
        <div class="reviews-section-header">
          <h3
            class="reviews-title"
            v-html="getTranslatedContent('REVIEWS_what_do_customers_say')"
          >
          </h3>
          <div class="rating-wrapper">
            <div class="average-review-rating">
              <div class="average-rating">
                {{ averageRating && averageRating.toFixed(1) }}
              </div>
            </div>
            <StarsRating :rating="averageRating && averageRating.toFixed(0)" />
          </div>
        </div>
      </div>
    </section>
    <section>
      <ReviewsSlider :reviews="fifteenRandomReviews" />
    </section>
  </Layout>
</template>

<style lang="scss" scoped>
@import '@design';
@import '@styles/author_text.scss';

.header {
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  height: 550px;
  width: 100%;
}

.header-block {
  width: 840px;
  height: 135px;
  // background: rgba(37, 44, 62, 0.36);
  padding-top: 9px;
  box-sizing: border-box;
  margin-top: 217px;
  margin-right: 300px;

  .header-title {
    font-family: Open Sans Bold, sans-serif;
    font-weight: bold;
    font-size: 48px;
    line-height: 62px;
    text-transform: uppercase;
    margin-left: 22px;
    color: $white;
  }

  .header-subtitle {
    font-family: Open Sans Semibold, sans-serif;
    font-weight: 600;
    font-size: 22px;
    line-height: 29px;
    margin-left: 25px;
    color: $white;
  }
}

.description-banner {
  margin: -99px 0 60px;
  position: relative;
  & .description {
    width: 1140px;
    min-height: 475px;
    height: auto;
    display: flex;
    flex-direction: row;
    border-radius: 20px;
    box-sizing: border-box;
    background: $white;
    box-shadow: 0 8px 12px rgba(76, 72, 124, 0.08);
  }
}

.description-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  margin: 31px 0 0 70px;
}

.banner-content {
  margin-right: 15px;
}

.banner-images {
  display: flex;
  align-items: center;
}

.banner-card {
  height: 150px;
  width: 230px;
  background: url('../../../assets/images/prague-card-rounded.png') no-repeat
    center;
  background-size: cover;
  border-radius: 10px;
  margin-bottom: 12px;
}

.banner-phone {
  height: 311px;
  width: 291px;
  background: url('../../../assets/images/phone-with-hand-right.png') no-repeat
    center;
  background-size: cover;
}

.banner-text {
  width: 500px;
}

.description-card-list {
  border-radius: 0px 20px 20px 0px;
  width: 529px;
  padding: 34px 32px;
  background: $dark-blue;
}

.tips-details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-left: 15px;
  user-select: none;
}

.tips-details ul li {
  font-family: Open Sans Semibold, sans-serif;
  font-weight: normal;
  font-size: 16px;
  line-height: 23px;
  color: $white;
  margin-bottom: 5px;
  padding-left: 10px;
}

.subtext {
  font-family: Open Sans, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 130%;
  color: white;
  margin-bottom: 20px;
  padding-left: 10px;
}

.section-title,
.reviews-title,
.banner-title {
  font-family: Open Sans Bold, sans-serif;
  font-weight: bold;
  font-size: 22px;
  line-height: 29px;
  text-transform: uppercase;
  color: $dark-blue;
  margin-bottom: 15px;
}

.banner-title {
  margin-bottom: 20px;
}

.section-title {
  margin-top: 80px;
}

.using-title {
  font-family: Open Sans Bold, sans-serif;
  font-weight: bold;
  font-size: 18px;
  line-height: 23px;
  text-transform: uppercase;
  color: $dark-blue;
  margin-top: 30px;
  margin-bottom: 30px;
}

::v-deep .banner-text,
::v-deep .saving-text {
  font-family: Open Sans, sans-serif;
  font-weight: normal;
  font-size: 14px;
  line-height: 23px;
  color: $black;
  user-select: none;

  & em {
    font-style: italic;
  }

  & a {
    color: $gray;
    text-decoration: none;

    &:hover {
      color: $orange;
    }
  }

  & ul {
    margin-left: 20px;
    list-style: initial;
  }

  & strong {
    font-family: Open Sans Bold, sans-serif;
    font-style: normal;
    font-weight: bold;
  }

  & .ql-align-center {
    text-align: center;
  }
}

.reviews-section {
  margin-top: 93px;
}

.reviews-section-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.reviews-title {
  margin-top: 15px;
}

.rating-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-left: auto;
}

.average-review-rating {
  margin-left: auto;
  margin-right: 12px;
  font-family: Open Sans Bold, sans-serif;
  font-weight: bold;
  font-size: 22px;
  line-height: 29px;
  color: $dark-blue;
}

@media only screen and (max-width: $screen-width-xs) {
  .main-container {
    width: 100%;
  }

  .header {
    height: 244px;
  }

  .header-block {
    width: 100%;
    margin-top: 70px;
    margin-right: 0;
    padding-top: 0;
    height: 125px;

    .header-title {
      font-family: Open Sans Bold, sans-serif;
      font-weight: bold;
      font-size: 26px;
      line-height: 34px;
      text-transform: uppercase;
      margin: 0px 8px 10px;
      color: $white;
    }

    .header-subtitle {
      font-family: Open Sans Semibold, sans-serif;
      font-weight: 600;
      font-size: 16px;
      line-height: 21px;
      color: $white;
      margin: 0 8px;
    }
  }

  .description-banner {
    margin: -20px 0 0px;

    & .description {
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: auto;
      padding: 0;
    }
  }

  .description-card {
    width: 100%;
    flex-direction: column;
    align-items: center;
    margin: 0;
  }

  .banner-images {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .banner-card,
  .banner-phone {
    margin-top: 0;
  }

  .banner-phone {
    height: 207px;
    width: 180px;
  }

  .banner-card {
    height: 120px;
    width: 174px;
    margin-left: 13px;
    border-radius: 10px;
  }

  .banner-content {
    text-align: center;
    margin: 0;
    padding: 0 8px;
  }

  .banner-title {
    font-size: 18px;
    line-height: 23px;
    margin: 15px 22px 5px;
  }

  .banner-text {
    width: 100%;
    text-align: left;
  }

  .description-card-list {
    border-radius: 0px;
    width: 100%;
    padding: 34px 0;
    background: $dark-blue;
    border-radius: 0 0 20px 20px;
  }

  .tips-details {
    margin: 0 12px 0 30px;
  }

  .tips-details ul li {
    margin-bottom: 5px;
  }

  .section-title {
    margin-top: 40px;
  }

  .using-title {
    margin-top: 30px;
  }

  .reviews-section {
    margin-top: 66px;
  }

  .reviews-section-header {
    flex-direction: column;
  }

  .rating-wrapper {
    margin: 0 auto 0 0;
    justify-content: start;
  }

  .average-review-rating {
    margin-right: 12px;
  }
}
</style>
